import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { I18nConfigService, SupportedLanguage } from './i18n-config.service';

/**
 * Facade for language operations in the application.
 *
 * This broker centralizes language changes and validation while preserving
 * the current app behavior. Existing components can migrate progressively
 * from direct TranslateService usage to this service.
 */
@Injectable({
	providedIn: 'root'
})
export class I18nBrokerService {
	private readonly currentLanguageSubject: BehaviorSubject<SupportedLanguage>;
	readonly currentLanguage$: Observable<SupportedLanguage>;

	constructor(
		private readonly translateService: TranslateService,
		private readonly i18nConfig: I18nConfigService
	) {
		this.currentLanguageSubject = new BehaviorSubject<SupportedLanguage>(
			this.i18nConfig.getDefaultLanguage()
		);
		this.currentLanguage$ = this.currentLanguageSubject.asObservable();
		this.registerSupportedLanguages();
	}

	/**
	 * Returns current language tracked by the broker.
	 */
	get currentLanguage(): SupportedLanguage {
		return this.currentLanguageSubject.value;
	}

	/**
	 * Sets initial language based on country and falls back to default language.
	 */
	initializeByCountry(country: string): Observable<SupportedLanguage> {
		const resolvedLanguage = this.resolveLanguageByCountry(country);
		return this.changeLanguage(resolvedLanguage);
	}

	/**
	 * Resolves language for a country. If country is unknown, returns default language.
	 */
	resolveLanguageByCountry(country: string): SupportedLanguage {
		return this.i18nConfig.getDefaultLanguageByCountry(country) ?? this.i18nConfig.getDefaultLanguage();
	}

	/**
	 * Applies a language change with validation and fallback.
	 */
	changeLanguage(language: string): Observable<SupportedLanguage> {
		const safeLanguage = this.i18nConfig.isSupportedLanguage(language)
			? language
			: this.i18nConfig.getDefaultLanguage();

		this.translateService.setDefaultLang(safeLanguage);

		return this.translateService.use(safeLanguage).pipe(
			tap(() => {
				this.currentLanguageSubject.next(safeLanguage);
				this.syncDocumentLanguage(safeLanguage);
			}),
			map(() => safeLanguage),
			catchError(() => {
				const fallbackLanguage = this.i18nConfig.getDefaultLanguage();
				this.translateService.setDefaultLang(fallbackLanguage);
				this.currentLanguageSubject.next(fallbackLanguage);
				this.syncDocumentLanguage(fallbackLanguage);
				return of(fallbackLanguage);
			})
		);
	}

	/**
	 * Returns all supported language codes for UI selectors.
	 */
	getSupportedLanguageCodes(): SupportedLanguage[] {
		return this.i18nConfig.getLanguageCodes();
	}

	/**
	 * Returns true when a language is allowed by application configuration.
	 */
	isSupportedLanguage(language: string): language is SupportedLanguage {
		return this.i18nConfig.isSupportedLanguage(language);
	}

	/**
	 * Returns the dealer language code for a given country.
	 * Use this instead of LanguageDealer enum for consistency.
	 * @param country - Country code (e.g., 'BRA', 'ARG', 'COL', 'CHL')
	 * @returns Language code for the country
	 */
	getDealerLanguageByCountry(country: string): string {
		return this.resolveLanguageByCountry(country);
	}

	private registerSupportedLanguages(): void {
		this.translateService.addLangs(this.i18nConfig.getLanguageCodes());
	}

	private syncDocumentLanguage(language: SupportedLanguage): void {
		if (typeof document !== 'undefined') {
			document.documentElement.lang = language;
		}
	}
}
